import PropTypes from 'prop-types'

/**
 * Service Coverage Heatmap
 *
 * Shows color-coded table of service availability across sub-locations
 */
function ServiceCoverageHeatmap({ data, title, narrative, templateText }) {
  if (!data || !data.heatmap || data.heatmap.locations.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">
          No service coverage heatmap data available.
        </p>
      </div>
    )
  }

  const { locations, services, data: heatmapData } = data.heatmap

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

      {/* Narrative or template text */}
      {(narrative || templateText) && (
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            {narrative || templateText}
          </p>
        </div>
      )}

      {/* Heatmap Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">
                Location
              </th>
              {services.map((service, index) => (
                <th
                  key={index}
                  className="px-2 py-3 text-center text-xs font-medium text-gray-700 border-r border-gray-200"
                  title={service.name}
                >
                  <div className="flex flex-col items-center">
                    {service.icon && (
                      <span className="text-lg mb-1">{service.icon}</span>
                    )}
                    <span className="max-w-[80px] truncate">
                      {service.name}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {locations.map((location, locationIndex) => (
              <tr key={locationIndex} className="border-t border-gray-200">
                <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap border-r border-gray-200">
                  {location}
                </td>
                {services.map((service, serviceIndex) => {
                  const cellData = heatmapData.find(
                    d =>
                      d.locationIndex === locationIndex &&
                      d.serviceIndex === serviceIndex
                  )
                  return (
                    <td
                      key={serviceIndex}
                      className="text-center p-2 border-r border-gray-200"
                    >
                      <div
                        className="w-16 h-8 rounded flex items-center justify-center text-white text-xs font-medium mx-auto"
                        style={{
                          backgroundColor: cellData?.color || '#6b7280',
                        }}
                        title={`${cellData?.serviceName}: ${cellData?.percentage || 0}%`}
                      >
                        {cellData?.percentage || 0}%
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Service availability across locations
        </p>
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-600 rounded"></div>
            <span className="text-gray-700">Good (≥80%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-gray-700">Moderate (50-79%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-600 rounded"></div>
            <span className="text-gray-700">Poor (&lt;50%)</span>
          </div>
        </div>
      </div>
    </div>
  )
}

ServiceCoverageHeatmap.propTypes = {
  data: PropTypes.shape({
    heatmap: PropTypes.shape({
      locations: PropTypes.arrayOf(PropTypes.string).isRequired,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          icon: PropTypes.string,
        })
      ).isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          locationIndex: PropTypes.number.isRequired,
          serviceIndex: PropTypes.number.isRequired,
          locationName: PropTypes.string.isRequired,
          serviceName: PropTypes.string.isRequired,
          percentage: PropTypes.number.isRequired,
          color: PropTypes.string.isRequired,
          status: PropTypes.string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  narrative: PropTypes.string,
  templateText: PropTypes.string,
}

export default ServiceCoverageHeatmap
